import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Matches ContactData.js's NEXT_PUBLIC_API_URL-equivalent env var/sentinel
// pattern — see its own comment for the Dockerfile.prod/docker-entrypoint.sh
// runtime-replacement story.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:21000';

/*
 * Site Traffic: one page-view beacon per route change. Unlike wrench-www
 * (full page loads, mount-once is enough — see its TrafficBeacon.tsx), this
 * is a react-router SPA, so internal navigation never remounts the app —
 * `useLocation()`'s pathname is what actually changes per "page view" here.
 *
 * No cookies and nothing stored in the browser — wrench-api's app/traffic.py
 * explains what's kept server-side. Sent as text/plain via sendBeacon, so it
 * needs no CORS preflight and survives the visitor navigating away. Skipped
 * when the browser asks not to be tracked.
 */

function trackingAllowed() {
    const nav = navigator;
    const dnt = nav.doNotTrack ?? window.doNotTrack ?? nav.msDoNotTrack;
    return !(dnt === '1' || dnt === 'yes' || nav.globalPrivacyControl === true);
}

export default function TrafficBeacon() {
    const { pathname, search } = useLocation();
    const lastSent = useRef(null);

    useEffect(() => {
        if (lastSent.current === pathname || !trackingAllowed()) return;
        lastSent.current = pathname;
        const params = new URLSearchParams(search);
        const body = JSON.stringify({
            p: pathname,
            r: document.referrer,
            h: window.location.hostname,
            u: params.get('utm_source') ?? '',
            w: window.innerWidth,
            s: 'www-ng',
        });
        const url = `${API_BASE_URL}/website/track`;
        try {
            if (navigator.sendBeacon?.(url, body)) return;
        } catch {
            // fall through to fetch
        }
        fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
    }, [pathname, search]);

    return null;
}
